import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { OrganizationSettingsService } from '../../../core/services/organization/organization-settings.service';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
})
export class PaginationComponent implements OnInit, OnChanges {
  // Inputs
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() pageSize: number = 10;
  @Input() pageSizes: number[] = [5, 10, 15, 20, 25, 50];
  @Input() isLoading: boolean = false;
  @Input() isEmpty: boolean = false;
  @Input() hideOnMobile: boolean = true; // Controls max-md:hidden class
  @Input() totalItems: number = 0; // Total number of items for summary text

  // Outputs
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() goToPageClick = new EventEmitter<number>();

  // Internal page input for "Go to page" feature
  pageInput: number = 1;

  constructor(public settingsService: OrganizationSettingsService) {}

  ngOnInit(): void {
    if (this.settingsService.defaultPageSize()) {
      const globalSize = this.settingsService.defaultPageSize();
      if (!this.pageSizes.includes(globalSize)) {
        this.pageSizes = [...this.pageSizes, globalSize].sort((a, b) => a - b);
      }
    }
  }

  ngOnChanges() {
    // Sync internal page input with current page
    this.pageInput = this.currentPage;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  onPageChange(newPage: number) {
    if (newPage >= 1 && newPage <= this.totalPages && newPage !== this.currentPage) {
      this.pageChange.emit(newPage);
    }
  }

  onPageSizeChange() {
    this.pageSizeChange.emit(this.pageSize);
  }

  goToPage() {
    if (this.pageInput >= 1 && this.pageInput <= this.totalPages) {
      this.goToPageClick.emit(this.pageInput);
    } else {
      // Reset to valid range
      if (this.pageInput < 1) this.pageInput = 1;
      if (this.pageInput > this.totalPages) this.pageInput = this.totalPages;
    }
  }

  shouldShowPageNumber(pageNum: number): boolean {
    return (
      pageNum === 1 ||
      pageNum === this.totalPages ||
      (pageNum >= this.currentPage - 1 && pageNum <= this.currentPage + 1)
    );
  }

  shouldShowLeftEllipsis(pageNum: number): boolean {
    return pageNum === 1 && this.currentPage > 3;
  }

  shouldShowRightEllipsis(pageNum: number): boolean {
    return pageNum === this.totalPages && this.currentPage < this.totalPages - 2;
  }

  // Summary text calculations
  get showSummary(): boolean {
    return this.totalItems > 0;
  }

  get summaryStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get summaryEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }
}

